import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import { AIModel } from '../types/models';
import { modelService } from '../services/modelService';

interface ModelState {
  models: AIModel[];
  selectedModel: string | null;
  isLoading: boolean;
  error: string | null;
}

type ModelAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_MODELS'; payload: AIModel[] }
  | { type: 'SET_SELECTED_MODEL'; payload: string | null }
  | { type: 'RESET_STATE' };

const initialState: ModelState = {
  models: [],
  selectedModel: null,
  isLoading: false,
  error: null,
};

function modelReducer(state: ModelState, action: ModelAction): ModelState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'SET_MODELS':
      return { ...state, models: action.payload, isLoading: false, error: null };
    case 'SET_SELECTED_MODEL':
      return { ...state, selectedModel: action.payload };
    case 'RESET_STATE':
      return initialState;
    default:
      return state;
  }
}

interface ModelContextType extends ModelState {
  loadModels: () => Promise<void>;
  setSelectedModel: (modelId: string) => void;
  clearError: () => void;
  getSelectedModel: () => AIModel | null;
  refreshModels: () => Promise<void>;
}

const ModelContext = createContext<ModelContextType | undefined>(undefined);

export function useModel() {
  const context = useContext(ModelContext);
  if (!context) {
    throw new Error('useModel must be used within a ModelProvider');
  }
  return context;
}

interface ModelProviderProps {
  children: ReactNode;
}

export function ModelProvider({ children }: ModelProviderProps) {
  const [state, dispatch] = useReducer(modelReducer, initialState);
  const hasLoadedModels = useRef(false);

  // Load selected model from localStorage on mount
  useEffect(() => {
    const savedModel = localStorage.getItem('selectedModel');
    if (savedModel) {
      dispatch({ type: 'SET_SELECTED_MODEL', payload: savedModel });
    }
  }, []);

  // Save selected model to localStorage whenever it changes
  useEffect(() => {
    if (state.selectedModel) {
      localStorage.setItem('selectedModel', state.selectedModel);
    }
  }, [state.selectedModel]);

  const loadModels = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      // Use the modelService to load models
      const models = await modelService.getModels({
        is_active: true,
        include_public: true,
      });

      dispatch({ type: 'SET_MODELS', payload: models });

      // Only set default model if no model is selected AND models haven't been loaded before
      const currentSelectedModel = localStorage.getItem('selectedModel');
      if (!currentSelectedModel && !hasLoadedModels.current && models.length > 0) {
        // Find the default model or use the first active model
        const defaultModel = models.find(model => model.is_default) || models[0];
        if (defaultModel) {
          dispatch({ type: 'SET_SELECTED_MODEL', payload: defaultModel.id });
        }
      }

      hasLoadedModels.current = true;
    } catch (error) {
      dispatch({
        type: 'SET_ERROR',
        payload: error instanceof Error ? error.message : 'Failed to load models',
      });
    }
  }, []);

  const setSelectedModel = useCallback((modelId: string) => {
    dispatch({ type: 'SET_SELECTED_MODEL', payload: modelId });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: 'SET_ERROR', payload: null });
  }, []);

  const getSelectedModel = useCallback((): AIModel | null => {
    if (!state.selectedModel || state.models.length === 0) {
      return null;
    }
    return state.models.find(model => model.id === state.selectedModel) || null;
  }, [state.selectedModel, state.models]);

  const refreshModels = useCallback(async () => {
    // Clear cache to force fresh fetch
    modelService.clearCache();
    await loadModels();
  }, [loadModels]);

  // AuthenticatedProviders ensures this only mounts inside ProtectedRoute
  // So we can safely load models without auth checks
  useEffect(() => {
    loadModels();
  }, [loadModels]);

  const value: ModelContextType = {
    ...state,
    loadModels,
    setSelectedModel,
    clearError,
    getSelectedModel,
    refreshModels,
  };

  return <ModelContext.Provider value={value}>{children}</ModelContext.Provider>;
}
